import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Button, Table, Spin, message } from 'antd';
import * as XLSX from 'xlsx';
import pdfMake from 'pdfmake/build/pdfmake';

interface StudentAbsenceRow {
  Id?: string;
  NgayGiang?: string;
  MaSV?: string;
  HoTen?: string;
  NgaySinh?: string;
  LopSinhHoat?: string;
  TenLop?: string;
  LopHocPhanID?: string;
  TenLopHP?: string;
  TenBaiGiang?: string;
  GiangVien?: string;
  BuoiGiang?: string;
  BuoiSo?: number;
  SoTietLT?: number;
  SoTietTH?: number;
  Vang?: boolean | number | string;
  Muon?: boolean | number | string;
  BoTiet?: boolean | number | string;
  LyDo?: string;
  KhongDat?: boolean | number | string;
  CoHoaChat?: boolean | number | string;
}

const apiBase = '/api/v1';

export default function StudentAbsenceStatisticPage() {
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [classSections, setClassSections] = useState<any[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string | undefined>();
  const [selectedSubject, setSelectedSubject] = useState<string | undefined>();
  const [selectedClassSection, setSelectedClassSection] = useState<string | undefined>();
  const [selectedClassSectionType, setSelectedClassSectionType] = useState<string | undefined>();
  const [data, setData] = useState<StudentAbsenceRow[]>([]);

  useEffect(() => {
    fetch(`${apiBase}/courses`).then(r => r.json()).then(json => {
      if (Array.isArray(json?.data)) setCourses(json.data);
      else setCourses([]);
    });
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      fetch(`${apiBase}/subjects/by-khoahoc/${selectedCourse}`).then(r => r.json()).then(json => {
        if (Array.isArray(json)) setSubjects(json);
        else if (json && Array.isArray(json.data)) setSubjects(json.data);
        else setSubjects([]);
      });
    } else {
      setSubjects([]);
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse && selectedSubject) {
      fetch(`${apiBase}/lophocphan/by-khoahoc-monhoc?khoaHocId=${selectedCourse}&monHocId=${selectedSubject}`)
        .then(r => r.json())
        .then(json => {
          if (json && Array.isArray(json.options)) {
            setClassSections(json.options);
          } else {
            setClassSections([]);
          }
        });
    } else {
      setClassSections([]);
    }
  }, [selectedCourse, selectedSubject]);

  useEffect(() => {
    if (selectedClassSection) {
      setData([]);
      setLoading(true);
      fetch(`${apiBase}/statistic/student-absence?courseId=${selectedCourse||''}&subjectId=${selectedSubject||''}&classSectionId=${selectedClassSection}&classSectionType=${selectedClassSectionType||''}`)
        .then(res => res.json())
        .then(json => {
          if (json && json.error) {
            message.error('Lỗi backend: ' + json.error);
            setData([]);
          } else if (Array.isArray(json)) {
            setData(json.map((row: StudentAbsenceRow) => {
              if (!row.LopHocPhanID && selectedClassSection) {
                return { ...row, LopHocPhanID: selectedClassSection };
              }
              return row;
            }));
          } else {
            setData([]);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [selectedClassSection, selectedClassSectionType, selectedCourse, selectedSubject]);

  const handleStatistic = async () => {
    setSelectedClassSection(undefined);
    setData([]);
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/statistic/student-absence?courseId=${selectedCourse||''}&subjectId=${selectedSubject||''}`);
      const json = await res.json();
      if (json && json.error) {
        message.error('Lỗi backend: ' + json.error);
        setData([]);
      } else if (Array.isArray(json)) {
        setData(json);
      } else {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  };

  function formatDate(dateStr?: string) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('vi-VN');
  }

  function renderCheckmark(val: any) {
    return val === true || val === 1 || val === '1' ? '✔️' : '';
  }

  function unique(arr: StudentAbsenceRow[], key: keyof StudentAbsenceRow) {
    return Array.from(new Set(arr.map(item => item[key]))).filter((v): v is string|number => typeof v === 'string' || typeof v === 'number').map(value => ({ text: String(value), value }));
  }

  const columns = [
    { title: 'Ngày giảng', dataIndex: 'NgayGiang', key: 'NgayGiang', render: formatDate, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => new Date(a.NgayGiang || '').getTime() - new Date(b.NgayGiang || '').getTime(), filters: unique(data, 'NgayGiang'), onFilter: (value: any, record: StudentAbsenceRow) => record.NgayGiang === value },
    { title: 'Mã SV', dataIndex: 'MaSV', key: 'MaSV', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.MaSV||'').localeCompare(b.MaSV||''), filters: unique(data, 'MaSV'), onFilter: (value: any, record: StudentAbsenceRow) => record.MaSV === value },
    { title: 'Họ tên', dataIndex: 'HoTen', key: 'HoTen', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.HoTen||'').localeCompare(b.HoTen||''), filters: unique(data, 'HoTen'), onFilter: (value: any, record: StudentAbsenceRow) => record.HoTen === value },
    { title: 'Ngày sinh', dataIndex: 'NgaySinh', key: 'NgaySinh', render: formatDate, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => new Date(a.NgaySinh || '').getTime() - new Date(b.NgaySinh || '').getTime(), filters: unique(data, 'NgaySinh'), onFilter: (value: any, record: StudentAbsenceRow) => record.NgaySinh === value },
    { title: 'Lớp sinh hoạt', dataIndex: 'LopSinhHoat', key: 'LopSinhHoat', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.LopSinhHoat||'').localeCompare(b.LopSinhHoat||''), filters: unique(data, 'LopSinhHoat'), onFilter: (value: any, record: StudentAbsenceRow) => record.LopSinhHoat === value },
    { title: 'Tên lớp', dataIndex: 'TenLop', key: 'TenLop', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.TenLop||'').localeCompare(b.TenLop||''), filters: unique(data, 'TenLop'), onFilter: (value: any, record: StudentAbsenceRow) => record.TenLop === value },
    { title: 'Lớp học phần', dataIndex: 'LopHocPhanID', key: 'LopHocPhanID', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.LopHocPhanID||'').localeCompare(b.LopHocPhanID||''), filters: unique(data, 'LopHocPhanID'), onFilter: (value: any, record: StudentAbsenceRow) => record.LopHocPhanID === value },
    { title: 'Tên lớp HP', dataIndex: 'TenLopHP', key: 'TenLopHP', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.TenLopHP||'').localeCompare(b.TenLopHP||''), filters: unique(data, 'TenLopHP'), onFilter: (value: any, record: StudentAbsenceRow) => record.TenLopHP === value },
    { title: 'Tên bài giảng', dataIndex: 'TenBaiGiang', key: 'TenBaiGiang', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.TenBaiGiang||'').localeCompare(b.TenBaiGiang||''), filters: unique(data, 'TenBaiGiang'), onFilter: (value: any, record: StudentAbsenceRow) => record.TenBaiGiang === value },
    { title: 'Giảng viên', dataIndex: 'GiangVien', key: 'GiangVien', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.GiangVien||'').localeCompare(b.GiangVien||''), filters: unique(data, 'GiangVien'), onFilter: (value: any, record: StudentAbsenceRow) => record.GiangVien === value },
    { title: 'Buổi giảng', dataIndex: 'BuoiGiang', key: 'BuoiGiang', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.BuoiGiang||'').localeCompare(b.BuoiGiang||''), filters: unique(data, 'BuoiGiang'), onFilter: (value: any, record: StudentAbsenceRow) => record.BuoiGiang === value },
    { title: 'Buổi số', dataIndex: 'BuoiSo', key: 'BuoiSo', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.BuoiSo||0)-Number(b.BuoiSo||0), filters: unique(data, 'BuoiSo'), onFilter: (value: any, record: StudentAbsenceRow) => record.BuoiSo === value },
    { title: 'Số tiết LT', dataIndex: 'SoTietLT', key: 'SoTietLT', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.SoTietLT||0)-Number(b.SoTietLT||0), filters: unique(data, 'SoTietLT'), onFilter: (value: any, record: StudentAbsenceRow) => record.SoTietLT === value },
    { title: 'Số tiết TH', dataIndex: 'SoTietTH', key: 'SoTietTH', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.SoTietTH||0)-Number(b.SoTietTH||0), filters: unique(data, 'SoTietTH'), onFilter: (value: any, record: StudentAbsenceRow) => record.SoTietTH === value },
    { title: 'Vắng', dataIndex: 'Vang', key: 'Vang', render: renderCheckmark, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.Vang||0)-Number(b.Vang||0), filters: [{text:'Có',value:1},{text:'Không',value:0}], onFilter: (value: any, record: StudentAbsenceRow) => record.Vang == value },
    { title: 'Muộn', dataIndex: 'Muon', key: 'Muon', render: renderCheckmark, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.Muon||0)-Number(b.Muon||0), filters: [{text:'Có',value:1},{text:'Không',value:0}], onFilter: (value: any, record: StudentAbsenceRow) => record.Muon == value },
    { title: 'Bỏ tiết', dataIndex: 'BoTiet', key: 'BoTiet', render: renderCheckmark, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.BoTiet||0)-Number(b.BoTiet||0), filters: [{text:'Có',value:1},{text:'Không',value:0}], onFilter: (value: any, record: StudentAbsenceRow) => record.BoTiet == value },
    { title: 'Lý do', dataIndex: 'LyDo', key: 'LyDo', sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => (a.LyDo||'').localeCompare(b.LyDo||''), filters: unique(data, 'LyDo'), onFilter: (value: any, record: StudentAbsenceRow) => record.LyDo === value },
    { title: 'Không đạt', dataIndex: 'KhongDat', key: 'KhongDat', render: renderCheckmark, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.KhongDat||0)-Number(b.KhongDat||0), filters: [{text:'Có',value:1},{text:'Không',value:0}], onFilter: (value: any, record: StudentAbsenceRow) => record.KhongDat == value },
    { title: 'Có Hóa chất', dataIndex: 'CoHoaChat', key: 'CoHoaChat', render: renderCheckmark, sorter: (a: StudentAbsenceRow, b: StudentAbsenceRow) => Number(a.CoHoaChat||0)-Number(b.CoHoaChat||0), filters: [{text:'Có',value:1},{text:'Không',value:0}], onFilter: (value: any, record: StudentAbsenceRow) => record.CoHoaChat == value }
  ];

  const handleExportExcel = () => {
    if (!data.length) {
      message.warning('Không có dữ liệu để xuất!');
      return;
    }
    let tenLopHP = '';
    if (selectedClassSection) {
      const found = classSections.find((cs: any) => (cs.value || cs.LopHocPhanID) === selectedClassSection);
      tenLopHP = found ? (found.TenLopHP || found.TenLopHocPhan || found.TenLop || found.label) : '';
    }
    const tenMonHoc = selectedSubject ? (subjects.find((s: any) => s.MonHocID === selectedSubject)?.TenMonHoc || '') : '';
    const tenKhoaHoc = selectedCourse ? (courses.find((c: any) => c.KhoaHocID === selectedCourse)?.TenKhoaHoc || '') : '';
    const wsData = [
      [
        'UBND TỈNH THÁI BÌNH', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      ],
      [
        'TRƯỜNG CAO ĐẲNG Y TẾ', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      ],
      [
        'THỐNG KÊ SINH VIÊN VẮNG, MUỘN THEO MÔN HỌC', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', ''
      ],
      [
        `Khóa học: ${tenKhoaHoc}`, '', '', '', '', '', '', '', `Môn học: ${tenMonHoc}`, '', '', '', '', '', '', '', `Lớp HP: ${tenLopHP}`, ''
      ],
      []
    ];
    wsData.push([
      'Ngày giảng', 'Mã SV', 'Họ tên', 'Ngày sinh', 'Lớp sinh hoạt', 'Tên lớp', 'Lớp học phần', 'Tên lớp HP', 'Tên bài giảng', 'Giảng viên', 'Buổi giảng', 'Buổi số', 'Số tiết LT', 'Số tiết TH', 'Vắng', 'Muộn', 'Bỏ tiết', 'Lý do', 'Không đạt', 'Có Hóa chất'
    ]);
    wsData.push(...data.map(row => [
      row.NgayGiang ? formatDate(row.NgayGiang) : '',
      row.MaSV ? String(row.MaSV) : '',
      row.HoTen ? String(row.HoTen) : '',
      row.NgaySinh ? formatDate(row.NgaySinh) : '',
      row.LopSinhHoat ? String(row.LopSinhHoat) : '',
      row.TenLop ? String(row.TenLop) : '',
      row.LopHocPhanID ? String(row.LopHocPhanID) : '',
      row.TenLopHP ? String(row.TenLopHP) : '',
      row.TenBaiGiang ? String(row.TenBaiGiang) : '',
      row.GiangVien ? String(row.GiangVien) : '',
      row.BuoiGiang ? String(row.BuoiGiang) : '',
      row.BuoiSo !== undefined && row.BuoiSo !== null ? String(row.BuoiSo) : '',
      row.SoTietLT !== undefined && row.SoTietLT !== null ? String(row.SoTietLT) : '',
      row.SoTietTH !== undefined && row.SoTietTH !== null ? String(row.SoTietTH) : '',
      row.Vang ? '✔️' : '',
      row.Muon ? '✔️' : '',
      row.BoTiet ? '✔️' : '',
      row.LyDo ? String(row.LyDo) : '',
      row.KhongDat ? '✔️' : '',
      row.CoHoaChat ? '✔️' : ''
    ]));
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!merges'] = [
      { s: { r:0, c:0 }, e: { r:0, c:17 } },
      { s: { r:1, c:0 }, e: { r:1, c:17 } },
      { s: { r:2, c:0 }, e: { r:2, c:17 } },
      { s: { r:3, c:0 }, e: { r:3, c:7 } },
      { s: { r:3, c:8 }, e: { r:3, c:12 } },
      { s: { r:3, c:16 }, e: { r:3, c:17 } }
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Thống kê vắng muộn');
    XLSX.writeFile(wb, 'ThongKeVangMuon.xlsx');
  };

  const handleExportPDF = () => {
    if (!data.length) {
      message.warning('Không có dữ liệu để in!');
      return;
    }
    let tenLopHP = '';
    if (selectedClassSection) {
      const found = classSections.find((cs: any) => (cs.value || cs.LopHocPhanID) === selectedClassSection);
      tenLopHP = found ? (found.TenLopHP || found.TenLopHocPhan || found.TenLop || found.label) : '';
    }
    const tenMonHoc = selectedSubject ? (subjects.find((s: any) => s.MonHocID === selectedSubject)?.TenMonHoc || '') : '';
    const tenKhoaHoc = selectedCourse ? (courses.find((c: any) => c.KhoaHocID === selectedCourse)?.TenKhoaHoc || '') : '';
    const now = new Date();
    const todayStr = `Thái Bình, ngày ${now.getDate()} tháng ${now.getMonth() + 1} năm ${now.getFullYear()}`;
    const tableBody = [
      [
        { text: 'Ngày giảng', style: 'tableHeader' },
        { text: 'Mã SV', style: 'tableHeader' },
        { text: 'Họ tên', style: 'tableHeader' },
        { text: 'Ngày sinh', style: 'tableHeader' },
        { text: 'Lớp sinh hoạt', style: 'tableHeader' },
        { text: 'Tên lớp', style: 'tableHeader' },
        { text: 'Lớp học phần', style: 'tableHeader' },
        { text: 'Tên lớp HP', style: 'tableHeader' },
        { text: 'Tên bài giảng', style: 'tableHeader' },
        { text: 'Giảng viên', style: 'tableHeader' },
        { text: 'Buổi giảng', style: 'tableHeader' },
        { text: 'Buổi số', style: 'tableHeader' },
        { text: 'Số tiết LT', style: 'tableHeader' },
        { text: 'Số tiết TH', style: 'tableHeader' },
        { text: 'Vắng', style: 'tableHeader' },
        { text: 'Muộn', style: 'tableHeader' },
        { text: 'Bỏ tiết', style: 'tableHeader' },
        { text: 'Lý do', style: 'tableHeader' },
        { text: 'Không đạt', style: 'tableHeader' },
        { text: 'Có Hóa chất', style: 'tableHeader' }
      ],
      ...data.map(row => [
        row.NgayGiang ? formatDate(row.NgayGiang) : '',
        row.MaSV ? String(row.MaSV) : '',
        row.HoTen ? String(row.HoTen) : '',
        row.NgaySinh ? formatDate(row.NgaySinh) : '',
        row.LopSinhHoat ? String(row.LopSinhHoat) : '',
        row.TenLop ? String(row.TenLop) : '',
        row.LopHocPhanID ? String(row.LopHocPhanID) : '',
        row.TenLopHP ? String(row.TenLopHP) : '',
        row.TenBaiGiang ? String(row.TenBaiGiang) : '',
        row.GiangVien ? String(row.GiangVien) : '',
        row.BuoiGiang ? String(row.BuoiGiang) : '',
        row.BuoiSo !== undefined && row.BuoiSo !== null ? String(row.BuoiSo) : '',
        row.SoTietLT !== undefined && row.SoTietLT !== null ? String(row.SoTietLT) : '',
        row.SoTietTH !== undefined && row.SoTietTH !== null ? String(row.SoTietTH) : '',
        row.Vang ? '✔️' : '',
        row.Muon ? '✔️' : '',
        row.BoTiet ? '✔️' : '',
        row.LyDo ? String(row.LyDo) : '',
        row.KhongDat ? '✔️' : '',
        row.CoHoaChat ? '✔️' : ''
      ])
    ];
    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [30, 20, 30, 30],
      content: [
        { text: 'UBND TỈNH THÁI BÌNH', style: 'headerLeft' },
        { text: 'TRƯỜNG CAO ĐẲNG Y TẾ', style: 'headerLeft', margin: [0, 0, 0, 8] },
        { text: 'THỐNG KÊ SINH VIÊN VẮNG, MUỘN THEO MÔN HỌC', style: 'mainTitle' },
        {
          columns: [
            { width: '60%', text: [
              { text: 'Khóa học: ', bold: true }, tenKhoaHoc, '    ',
              { text: 'Môn học: ', bold: true }, tenMonHoc, '    ',
              { text: 'Lớp HP: ', bold: true }, tenLopHP
            ], style: 'infoLine' },
            { width: '40%', text: todayStr, alignment: 'right', italics: true, style: 'infoLine' }
          ],
          columnGap: 10,
          margin: [0, 0, 0, 8]
        },
        {
          style: 'tableRow',
          table: {
            headerRows: 1,
            widths: [60, 60, 90, 60, 70, 70, 70, 70, 90, 80, 60, 50, 50, 50, 40, 40, 50, 80, 50, 60],
            body: tableBody
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#e6f7ff' : null,
            hLineWidth: () => 0.8,
            vLineWidth: () => 0.8,
            hLineColor: () => '#444',
            vLineColor: () => '#444',
          },
          margin: [0, 8, 0, 8]
        }
      ],
      styles: {
        headerLeft: { fontSize: 11, bold: true, alignment: 'left' },
        mainTitle: { fontSize: 18, bold: true, alignment: 'center', margin: [0, 0, 0, 12] },
        infoLine: { fontSize: 12, margin: [0, 2, 0, 2] },
        tableHeader: { fillColor: '#e6f7ff', bold: true, fontSize: 10, color: '#005baa', alignment: 'center', margin: [0, 6, 0, 6] },
        tableCell: { fontSize: 10, alignment: 'center' },
        tableFooter: { fontSize: 10, bold: true, alignment: 'center' },
        tableRow: { fontSize: 10 },
      },
      defaultStyle: {
        font: 'Roboto',
        fontSize: 11
      }
    };
    pdfMake.createPdf(docDefinition).open();
  };

  return (
    <Card title="Thống kê sinh viên vắng, muộn theo môn học">
      <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
        <Col span={8}>
          <Select style={{ width: '100%' }} placeholder="Chọn khóa học" value={selectedCourse} onChange={v => { setSelectedCourse(v); setSelectedSubject(undefined); setSelectedClassSection(undefined); }} options={(Array.isArray(courses) ? courses : []).map((c: any) => ({ label: `${c.KhoaHocID} - ${c.TenKhoaHoc}`, value: c.KhoaHocID }))} allowClear />
        </Col>
        <Col span={8}>
          <Select style={{ width: '100%' }} placeholder="Chọn môn học" value={selectedSubject} onChange={v => { setSelectedSubject(v); setSelectedClassSection(undefined); }} options={(Array.isArray(subjects) ? subjects : []).map((s: any) => ({ label: `${s.MonHocID} - ${s.TenMonHoc}`, value: s.MonHocID }))} allowClear />
        </Col>
        <Col span={8}>
          <Select
            style={{ width: '100%' }}
            placeholder="Chọn lớp học phần hoặc nhóm thực hành"
            value={selectedClassSection}
            onChange={(value, option) => {
              setSelectedClassSection(value);
              setSelectedClassSectionType((option as any)?.type);
            }}
            options={(Array.isArray(classSections) ? classSections : []).map((cs: any) => ({
              label: cs.label || `${cs.LopHocPhanID || cs.value} - ${cs.TenLopHP || cs.TenLopHocPhan || cs.TenLop || cs.label}`,
              value: cs.value || cs.LopHocPhanID,
              type: cs.type,
              LopHocPhanID: cs.LopHocPhanID || cs.value
            }))}
            allowClear
          />
        </Col>
      </Row>
      <Button type="primary" onClick={handleStatistic} loading={loading}>Thống kê theo Khóa học, Môn học</Button>
      <Button style={{marginLeft:8}} onClick={handleExportExcel} disabled={!data.length}>Xuất Excel</Button>
      <Button style={{marginLeft:8}} onClick={handleExportPDF} disabled={!data.length}>In thống kê (PDF)</Button>
      <Spin spinning={loading}>
        <Table columns={columns} dataSource={Array.isArray(data) ? data : []} rowKey={r => r.Id ? String(r.Id) : (r.MaSV ? String(r.MaSV) : '') + (r.NgayGiang ? String(r.NgayGiang) : '')} style={{ marginTop: 16 }} bordered scroll={{ x: true }} pagination={false} />
      </Spin>
    </Card>
  );
} 